import { useMemo } from 'react';
import { useAppSelector } from 'store/hooks';
import { EMPTY_SELECTOR } from './cachedSelectorsUtils';
import { useCachedSelectedProject } from './useCachedSelectedProject';
import {
  BusinessAddressRequest,
  BusinessAddressResponse,
  InfoCompletenessScoring,
} from 'store/api/apiTypes';
import { rulesApiSlice } from 'store/api/rulesApiSlice';

/**
 * useCachedBusinessAddress
 * Returns cached Business Address rule data without triggering a network call.
 */
export const useCachedBusinessAddress = () => {
  const { selectedProject } = useCachedSelectedProject();
  const domain = selectedProject?.domain;
  const activeReport = useAppSelector(state => state.history.activeReport);
  const isGenerating = useAppSelector(
    state => state.reportGeneration.isGenerating
  );

  const queryArgs = useMemo<BusinessAddressRequest | null>(
    () => (domain ? { domain, reportId: activeReport?.id } : null),
    [domain, activeReport]
  );

  const selectRule = useMemo(
    () =>
      queryArgs
        ? rulesApiSlice.endpoints.businessAddress.select(queryArgs)
        : (EMPTY_SELECTOR as any),
    [queryArgs]
  );
  const selectResult = useAppSelector(selectRule as any) as {
    data: any;
    status: string;
    isLoading?: boolean;
    error?: any;
  };
  const { data, status, isLoading: loading, error } = selectResult;
  const businessAddress = data as BusinessAddressResponse | undefined;

  let passCount = 0;
  const rulesMap = businessAddress?.scoring_breakdown as
    | Record<string, InfoCompletenessScoring>
    | undefined;
  if (rulesMap) {
    Object.values(rulesMap).forEach(rule => {
      if (rule.score >= 8) {
        passCount += 1;
      }
    });
  }

  const isLoaded =
    !!businessAddress?.business_address_extraction && !loading && !isGenerating;
  const isLoading = loading || isGenerating;

  return {
    businessAddress,
    status,
    isLoading,
    isLoaded,
    error,
    queryArgs,
    companyConfidence:
      businessAddress?.business_address_identified?.confidence_level,
    companyEvidance:
      businessAddress?.business_address_identified?.evidence_strength,
    scoring: businessAddress?.scoring_breakdown,
    score: businessAddress?.final_score,
    urlsAnalyzed: businessAddress?.page_urls_analyzed,
    extraction: businessAddress?.business_address_extraction,
    identified: {
      primary:
        businessAddress?.business_address_identified?.primary_business_address,
      confidence_level:
        businessAddress?.business_address_identified?.confidence_level,
      evidence_strength:
        businessAddress?.business_address_identified?.evidence_strength,
    },
    overallAssessment: businessAddress?.overall_assessment,
    hasBusinessAddress: !!businessAddress,
    passed: passCount,
    rulesCount: Object.keys(businessAddress?.scoring_breakdown || {}).length,
  };
};
